import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import 'src/library//elements/d-section';
import 'src/library//elements/d-tooltip';
import 'src/library/elements/d-label';
import 'src/library/editors/elements/d-radio-cell';
import 'src/library/editors/elements/d-checkbox';
import 'src/library/editors/elements/d-edit-text';
import 'src/library/editors/elements/d-select-dropdown';
import 'src/library/elements/d-smooth-resize';
import 'src/library/fields/d-view-info';
import { LabeledElement } from 'src/library/abstracts/labeled-element';
import { t } from 'src/i18n';
import type { Feature } from '../../store/selectors/features/feature';
import type { SelectDropdownOption } from '../editors/elements/select-dropdown-option';
import { specialTermsOptions } from '../../store/selectors/features/specialTermsOptions';

// Ansatttall-terskler for prisgrupper. Gruppene er eksklusive øvre grenser
// (< EMPLOYEES_GROUP_A_MAX → gruppe a, osv.), lavest gruppe først.
const EMPLOYEES_GROUP_A_MAX = 4; //  1–3 ansatte
const EMPLOYEES_GROUP_B_MAX = 9; //  4–8
const EMPLOYEES_GROUP_C_MAX = 16; // 9–15
// Alt ≥ EMPLOYEES_GROUP_C_MAX faller i gruppe d (16–30).

/**
 * The `DUpgrade` class is a custom element component that extends `LabeledElement`.
 * It represents a user interface for selecting and managing feature upgrade options
 * such as modules, addons, and bundles. The component supports upgrading features with
 * additional configuration options and dispatches related events to communicate changes.
 *
 * @customElement d-upgrade
 * @extends LabeledElement
 * @fires cancel - Fires when the user cancels the operation. The event bubbles and is composed.
 * @fires add-features - Fires when the user upgrades features. The event contains details
 *                        of selected features and healthcare signatory settings.
 *                        The event bubbles and is composed.
 * @fires update-special-terms - Fires when the user updates the special terms. The event contains the
 *                                updated special terms and bubbles and is composed.
 *
 * The component includes several configurable states and properties, such as:
 * - Features, modules, and addons selection.
 * - Dialog mode, special terms input, healthcare options, and bundle configurations.
 * - UI styles applied to display the options and upgrade options.
 *
 * Usage of this component allows interaction with multiple selectable options, such as radiobuttons
 * and checkboxes, providing a detailed and customizable upgrade interface.
 */
@customElement('d-upgrade')
export class DUpgrade extends LabeledElement {
  static readonly styles = [
    ...LabeledElement.styles,
    css`
      .options {
        display: block;
        margin-bottom: -4px;
        line-height: 120%;
      }

      .option {
        display: flex;
        align-items: flex-start;
        box-sizing: border-box;
        min-height: var(--inputElementHeight);
        margin-bottom: 6px;
        padding: 6px 14px 0 0;
        cursor: pointer;
        color: hsla(1, 0%, 0%, 0.8);
        line-height: 120%;
      }

      .option:last-child {
        padding-right: 0;
      }

      d-radio-cell {
        margin-top: -3px;
        margin-right: 6px;
        margin-left: -1px;
      }

      d-checkbox {
        width: 30px;
        margin-top: -6px;
      }

      .option[selected] .radioIcon:before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: var(--themeColor);
      }

      .option-label {
        margin-bottom: 6px;
        font-weight: 200;
      }

      .option-label span:first-child {
        font-weight: 500;
        margin-right: 0.3em;
      }

      .comment {
        font-size: 13px;
        font-weight: 200;
      }

      .button {
        flex: none;
        margin-left: 6px;
        border-radius: 4px;
        background: var(--themeColor);
        padding: 8px 12px;
        font-size: 16px;
        font-weight: 500;
        color: white;
        cursor: pointer;
      }

      @media (hover: hover) {
        .button:hover {
          background: var(--themeColorDarkerTwo);
        }
      }
    `,
  ];

  @property({ type: Boolean })
  dialog = false;
  @property({ type: Number })
  employeesCount = 1;
  @property({ type: String })
  specialTerms: string | undefined = undefined;
  @property({ type: Array })
  features: Feature[] = [];
  @property({ type: Array })
  bundles: string[] = [];
  @property({ type: Array })
  modules: string[] = [];
  @property({ type: Array })
  addons: string[] = [];
  @property({ type: String })
  selectedFeature = 'BASIC';
  @property({ type: Array })
  selectedAddons: string[] = [];
  @property({ type: Array })
  healthcareSignatoryOptions: SelectDropdownOption[] = [];
  @property({ type: String })
  healthcareSignatory: string | undefined = undefined;

  @property({ type: Boolean })
  canUpgrade = false;
  @property({ type: String })
  actionText = t('library.upgrade.upgrade');

  private get bundleText() {
    return t('library.upgrade.bundleText');
  }

  private get moduleOptions() {
    return this.options(this.modules);
  }

  private get bundleOptions() {
    return this.options(this.bundles);
  }

  private get addonOptions() {
    return this.options(this.addons);
  }

  onCancel() {
    this.dispatchEvent(
      new CustomEvent('cancel', {
        bubbles: true,
        composed: true,
        detail: 'cancel',
      }),
    );
  }

  onUpgrade() {
    const features: string[] = [];
    if (this.selectedFeature) {
      features.push(this.selectedFeature);
    }
    if (this.selectedAddons.length) {
      this.selectedAddons.forEach((a) => {
        features.push(a);
      });
    }
    this.dispatchEvent(
      new CustomEvent<{ features: string[]; healthcareSignatory: string | undefined }>('add-features', {
        composed: true,
        bubbles: true,
        detail: {
          features,
          healthcareSignatory: this.healthcareSignatory,
        },
      }),
    );
    if (this.specialTerms) {
      this.dispatchEvent(
        new CustomEvent<{ specialTerms: string }>('update-special-terms', {
          composed: true,
          bubbles: true,
          detail: { specialTerms: this.specialTerms },
        }),
      );
    }
    if (this.selectedFeature === 'NORENGROS-NEW') {
      window.open('https://www.norengros.no/user/register', '_blank');
    }
    if (this.selectedFeature === 'NORENGROS-EXISTING') {
      window.open('https://www.norengros.no', '_blank');
    }
  }

  renderFeatureOptions(options) {
    return html`
      ${options.map(
        (item) =>
          html` <div
            class="option"
            @click=${() => this.toggleSelect(item.value)}
            ?selected=${item.value === this.selectedFeature}
          >
            <d-radio-cell ?checked=${item.value === this.selectedFeature}></d-radio-cell>
            <div>
              <div class="option-label"><span>${item.text}</span><span>${item.secondaryText}</span></div>
              <div class="comment">${item.description}</div>
            </div>
          </div>`,
      )}
    `;
  }

  renderModuleOptions() {
    return html` <d-section>
      <div class="options">${this.renderFeatureOptions(this.moduleOptions)}</div>
    </d-section>`;
  }

  renderAddons() {
    return html`
      ${this.addonOptions.map(
        (item) =>
          html` <div class="option" @click=${() => this.toggleAddon(item.value)}>
            <d-checkbox
              ?theme-page="${this.themePage}"
              ?checked=${this.selectedAddons.includes(item.value)}
            ></d-checkbox>
            <div>
              <div class="option-label"><span>${item.text}</span><span>${item.secondaryText}</span></div>
              <div class="comment">${item.description}</div>
            </div>
          </div>`,
      )}
    `;
  }

  renderBundleOptions() {
    return html` <d-section>
      <div class="options">
        ${this.renderFeatureOptions(this.bundleOptions)}
        ${this.addons.length ? html` ${this.renderAddons()} ` : nothing}
      </div>
    </d-section>`;
  }

  renderContent() {
    return html`
      <d-smooth-resize>
        ${this.moduleOptions.length ? html` ${this.renderModuleOptions()} ` : nothing}
        ${this.bundleOptions.length
          ? html`
              <d-section>
                <d-view-info .content=${this.bundleText}></d-view-info>
              </d-section>
              ${this.renderBundleOptions()}
            `
          : nothing}
        <d-section style="margin-bottom: 6px">
          <d-wrap split style="align-items: end">
            ${this.bundleOptions.length && this.specialTerms && this.canUpgrade
              ? html`
                  <d-select-dropdown
                    ?theme-page="${this.themePage}"
                    system-content
                    .label=${t('library.upgrade.memberDiscount')}
                    .options=${specialTermsOptions}
                    .value=${this.specialTerms ?? 'NONE'}
                    @value-changed=${(e: CustomEvent<{ value: string }>) => {
                      this.specialTerms = e.detail.value;
                    }}
                  ></d-select-dropdown>
                `
              : nothing}
            ${this.selectedFeature === 'HEALTHCARE' && this.canUpgrade
              ? html`
                  <d-select-dropdown
                    ?theme-page="${this.themePage}"
                    system-content
                    .label=${t('library.upgrade.agreementSignedBy')}
                    .options=${this.healthcareSignatoryOptions}
                    .value=${this.healthcareSignatory ?? ''}
                    @value-changed=${(e: CustomEvent<{ value: string }>) => {
                      this.healthcareSignatory = e.detail.value;
                    }}
                  ></d-select-dropdown>
                `
              : nothing}
            ${this.selectedFeature === 'NORENGROS-EXISTING'
              ? html` <d-view-info info-list .content=${t('library.upgrade.norengrosExistingInfo')}> </d-view-info> `
              : nothing}
            ${this.selectedFeature === 'NORENGROS-NEW'
              ? html` <d-view-info .content=${t('library.upgrade.norengrosNewInfo')}> </d-view-info> `
              : nothing}
            ${this.canUpgrade
              ? html`
                  ${this.selectedFeature !== 'NORENGROS'
                    ? html` <div
                        style="flex: 1; align-self: flex-end; display: flex; align-items: baseline; justify-content: flex-end"
                      >
                        ${this.dialog
                          ? html` <d-action plain @click=${() => this.onCancel()}>${t('content.cancel')}</d-action> `
                          : nothing}
                        <div class="button" @click=${() => this.onUpgrade()}>${this.actionText}</div>
                      </div>`
                    : nothing}
                `
              : html`
                  <div>
                    ${t('library.upgrade.noUpgradeAccessBefore')}
                    <d-action plain href="https://app.trinnvis.no/signup"
                      >${t('library.upgrade.trinnvisLinkText')}</d-action
                    >
                    ${t('library.upgrade.noUpgradeAccessAfter')}${this.dialog
                      ? html` <d-action plain @click=${() => this.onCancel()}>${t('content.close')}</d-action> `
                      : nothing}
                  </div>
                  <div></div>
                `}
          </d-wrap>
        </d-section>
      </d-smooth-resize>
    `;
  }

  private priceText(feature: Feature): string {
    if (feature.startPrice && feature.organizationPrice) {
      return t('library.upgrade.startPriceMonthly', {
        startPrice: feature.startPrice,
        organizationPrice: feature.organizationPrice,
      });
    }
    if (feature.organizationPrice) {
      return t('library.upgrade.pricePerMonth', { price: feature.organizationPrice });
    }
    if (feature.price) {
      return t('library.upgrade.pricePerMonthPerUser', { price: feature.price });
    } else if (feature.prices) {
      let priceGroup = 'd';
      let planSizeText = t('library.upgrade.planSize16to30');
      if (this.employeesCount < EMPLOYEES_GROUP_C_MAX) {
        priceGroup = 'c';
        planSizeText = t('library.upgrade.planSize9to15');
      }
      if (this.employeesCount < EMPLOYEES_GROUP_B_MAX) {
        priceGroup = 'b';
        planSizeText = t('library.upgrade.planSize4to8');
      }
      if (this.employeesCount < EMPLOYEES_GROUP_A_MAX) {
        priceGroup = 'a';
        planSizeText = t('library.upgrade.planSize1to3');
      }
      let priceType = 'none';
      if (this.specialTerms !== '' && this.specialTerms !== 'NONE') {
        priceType = 'member';
      }
      return t('library.upgrade.pricePerMonthForPersons', {
        price: feature.prices[priceGroup][priceType],
        planSize: planSizeText,
      });
    }
    return '';
  }

  private options(items: string[]) {
    return items.map((i) => {
      let text = '';
      let secondaryText = '';
      let description = '';
      const feature: Feature | undefined = this.features.find((f) => {
        return f.id === i;
      });
      if (feature) {
        text = feature.displayName;
        secondaryText = this.priceText(feature);
        description = feature.description;
      }
      return {
        value: i,
        text,
        secondaryText,
        description,
      };
    });
  }

  private toggleSelect(value: string) {
    if (this.selectedFeature !== value) {
      this.selectedFeature = value;
    }
  }

  private toggleAddon(value: string) {
    if (this.selectedAddons.includes(value)) {
      this.selectedAddons = this.selectedAddons.filter((a) => {
        return a !== value;
      });
    } else {
      const selectedAddons: string[] = [...this.selectedAddons];
      selectedAddons.push(value);
      this.selectedAddons = selectedAddons;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-upgrade': DUpgrade;
  }
}
